import UriJs from 'urijs';
import _ from 'lodash';

import getAxiosInstance from '../api/axios';
import utilities from './utilities';
import resolvePropertySearch from './utilitiesPropertySearch';
import {
  getPlaceSearch, getSearchResults, getPropertySearch, getPlaceAutocompleteSearch,
} from './utilitiesSearchGrid';
import {ALLOWED_PLACE_TYPES} from '../Components/Containers/Sitemap/constValues';

// Priority groups for non-addresses (lower index = higher priority)
const PLACE_TYPE_PRIORITY_GROUPS = [
  [
    'city',
    'municipality',
    'town',
    'borough',
    'balance',
    'zona urbana',
    'comunidad',
    'village',
    'undefined',
    'unincorporated',
  ],
  ['county'],
  ['county subdivision'],
  ['census designated place'],
  ['neighborhood'],
  ['state'],
];

const axios = getAxiosInstance('', true);
const Compare = (strA, strB) => {
  let result = 0;
  // eslint-disable-next-line no-plusplus
  for (let i = strA.length; i > 0; i--) {
    if (typeof strB[i] === 'undefined' || typeof strA[i] === 'undefined') {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (strA[i].toLowerCase() === strB[i].toLowerCase()) {
      // eslint-disable-next-line no-plusplus
      result++;
    }
    else {
      result += 4;
    }
  }
  return 1 - (result + 4 * Math.abs(strA.length - strB.length)) / (2 * (strA.length + strB.length));
};

const axiosCall = _.debounce(async (searchString, callback, mapboxToken, types = ['address']) => {
  const encodedString = encodeURIComponent(searchString);
  const url = new UriJs(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodedString}.json`)
    .search('?limit=10')
    .addSearch('autocomplete', 'true')
    .addSearch('country', 'US')
    .addSearch('types', types.join(','))
    .addSearch('access_token', mapboxToken)
    .normalizeSearch()
    .readable();

  const placeUrl = await getPlaceAutocompleteSearch();
  placeUrl.addSearch('search', encodedString);
  const startsWithLetter = /^[a-zA-Z]/.test(searchString.trim());

  let mapboxResponse;
  let gapiPlaces;

  // Execute both requests in parallel
  if (startsWithLetter) {
    [mapboxResponse, gapiPlaces] = await Promise.all([axios.get(url), getSearchResults(placeUrl)]);
  }
  else {
    [mapboxResponse, gapiPlaces] = await Promise.all([axios.get(url)]);
  }

  const mapboxData = mapboxResponse.data;

  // Merge responses - combine features from both sources
  const gapiFeatures = gapiPlaces?.features || [];

  const getPlaceType = feature => (feature.place_type?.[0] ?? '').toLowerCase();
  const isAddress = feature => getPlaceType(feature) === 'address';
  const getPlaceTypePriority = (feature) => {
    const type = getPlaceType(feature);
    const groupIndex = PLACE_TYPE_PRIORITY_GROUPS.findIndex(group => group.includes(type));
    return groupIndex === -1 ? 99 : groupIndex + 1;
  };

  const allFeatures = [...(mapboxData.features || []), ...gapiFeatures];
  const addresses = allFeatures.filter(isAddress);
  const nonAddresses = allFeatures.filter(feature => !isAddress(feature));

  // Recalculate scores for non-addresses to incorporate place type priority
  // Higher priority types get a larger boost (5 for priority 1, 4 for priority 2, etc.)
  // This ensures cities rank above county subdivisions even with slightly lower original scores
  nonAddresses.forEach((feature) => {
    const priority = getPlaceTypePriority(feature);
    const boost = priority <= 5 ? 6 - priority : -10;
    feature.adjustedScore = (feature.score ?? 0) + boost;
  });

  // Sort non-addresses by adjusted score (descending)
  nonAddresses.sort((a, b) => (b.adjustedScore ?? 0) - (a.adjustedScore ?? 0));

  const mergedFeatures = startsWithLetter ? [...nonAddresses, ...addresses] : [...addresses, ...nonAddresses];

  const data = {
    ...mapboxData,
    features: mergedFeatures,
  };

  callback(
    data.features && data.features.length
      ? (data.features = data.features.map(item => ({
        ...item,
        ...{place_name: (item.matching_place_name || item.place_name).replace(', United States', '')},
      })))
      : [],
  );
}, 300);

const validatePlaceAsUnicorporatedPart = place =>
  (ALLOWED_PLACE_TYPES.includes(place.placeType && place.placeType[0]) && place.generateUnincorporated === 0)
  || (!ALLOWED_PLACE_TYPES.includes(place.placeType && place.placeType[0]) && place.generateUnincorporated !== 1);

const mapboxAPI = {
  getResult: async (searchString = '', callback, types, mapboxToken) => {
    axiosCall(searchString, callback, types, mapboxToken);
  },
  getGAPIResult: async (result, placesSettings, setError, returnData = false) => {
    if (result.place_type.length && result.place_type[0] !== 'address') {
      if (returnData) {
        return {
          ...result,
          placeId: result.id,
        };
      }
      // If it's an active market then immediately redirect to the map.
      const isActiveMarket = !!placesSettings?.[result.id]?.baseCode;
      if (isActiveMarket) {
        return mapboxAPI.loadMapWithUrl(result);
      }
      // Not active: resolve by point-search, then prefer an active candidate, else fallback to clicked place.
      return mapboxAPI.getGAPIResultPlace(result, placesSettings, setError, returnData);
    }
    if (result.place_type.length && result.place_type[0] === 'address') {
      return mapboxAPI.getGAPIResultProperty(result, setError, returnData);
    }

    return '';
  },
  getGAPIResultPlace: async (result, placesSettings, setError, returnData = false) => {
    const url = await getPlaceSearch();
    let placeType;
    switch (result?.place_type?.[0]) {
      case 'county':
      case 'County':
        placeType = 'county';
        break;
      case 'state':
      case 'State':
        placeType = 'state';
        break;
      default:
        placeType = 'balance,city,municipality,town,zona-urbana,comunidad,village,undefined,borough,census-designated-place,community-development-district,county-subdivision';
        break;
    }
    url
      .addSearch('fields[]', 'generateUnincorporated')
      .addSearch('placeType', placeType)
      .addSearch('skipRelates', 1)
      .addSearch('point_search', `{"geometry":"POINT(${result.center[0]} ${result.center[1]})"}`);

    return getSearchResults(url, true)
      .then(async (data) => {
        if (!data) {
          setError('No matching results found');
          return;
        }
        data.sort((a, b) => Compare(b.title || '', result.text) - Compare(a.title || '', result.text));

        const activeCandidate = Array.isArray(data) ? data.find(d => !!placesSettings?.[d?.id]?.baseCode) : null;

        let item = activeCandidate || data[0];

        // Logic to choose item based on place_type
        if (result?.place_type?.[0] === 'locality') {
          // Prioritize place with 'county-subdivision' placeType
          const countySubdivision = data.find(d => d.placeType?.includes('county-subdivision'));
          if (countySubdivision) {
            item = countySubdivision;
          }
        }
        else if (data.length > 1) {
          // Select 'county-subdivision' only when this is the only result
          // Filter out county-subdivision if there are multiple results
          const filtered = data.filter(d => d.placeType?.[0] !== 'county-subdivision');
          if (filtered.length > 0) {
            // preserve activeCandidate if it exists; otherwise use best filtered match
            item = activeCandidate || filtered[0];
          }
          // If data.length === 1, item remains data[0] (could be county-subdivision)
        }

        if (!['state', 'county'].includes(placeType) && validatePlaceAsUnicorporatedPart(item)) {
          const state = item.id.toString().split('-')?.[0];
          const newUrl = await getPlaceSearch();
          newUrl.addSearch('skipRelates', 1).addSearch('id', `${state}-state-place-${item.fipsCode}u`);
          item = await getSearchResults(newUrl);
        }

        if (returnData) {
          // eslint-disable-next-line consistent-return
          return {
            ...item,
            placeId: item.id,
          };
        }

        // If we didn't find an active candidate, redirect to the originally clicked place (per product rules).
        if (!activeCandidate) {
          if (result?.url && typeof result.url === 'string') {
            // eslint-disable-next-line consistent-return
            return mapboxAPI.loadMapWithUrl(result);
          }
        }

        // eslint-disable-next-line consistent-return
        return mapboxAPI.loadMapWithUrl(item);
      })
      .catch((thrown) => {
        if (axios.isCancel(thrown)) {
          console.warn('Mapbox GL JS:', thrown.message);
        }
        else {
          console.error(thrown);
        }
      });
  },
  getGAPIResultProperty: async (result, setError, returnData = false) => {
    const url = await getPropertySearch();
    url.addSearch('skipRelates', 1).addSearch('state_env', utilities.getCurrentStateEnv());
    url.addSearch('allValues', 1);

    if (result.properties.accuracy === 'interpolated') {
      url
        .addSearch('proximity_search', `{"lat":${result.center[1]}, "lon":${result.center[0]}, "dist": 32}`)
        .addSearch('rows', 50);
    }
    else {
      url.addSearch('point_search', `{"geometry":"POINT(${result.center[0]} ${result.center[1]})"}`);
    }

    return getSearchResults(url, true)
      .then(async (data) => {
        if (!data) {
          setError('No matching results found');
          return;
        }

        let item;
        try {
          item = resolvePropertySearch(data, result, Compare);
        }
        catch (error) {
          console.error('Failed to resolve property search result, fallback to first item:', error);
          item = data[0];
        }

        if (!item) {
          item = data[0];
        }

        if (returnData) {
          // eslint-disable-next-line consistent-return
          return {
            ...item,
            placeId: item.id,
          };
        }

        // eslint-disable-next-line consistent-return
        return mapboxAPI.loadMapWithProperty(item);
      })
      .catch((thrown) => {
        if (axios.isCancel(thrown)) {
          console.warn('Mapbox GL JS:', thrown.message);
        }
        else {
          console.error(thrown);
        }
      });
  },
  loadMapWithUrl: item => window.location.assign(item.url),
  loadMapWithProperty: async (item) => {
    const placeUrl = await getPlaceSearch();
    placeUrl
      .addSearch('fields[]', 'generateUnincorporated')
      .addSearch('placeType', `${ALLOWED_PLACE_TYPES.join(',')},unincorporated`)
      .addSearch('skipRelates', 1)
      .addSearch('id', item.place.join(','));
    let resultPlace = await getSearchResults(placeUrl, true);
    if (resultPlace?.length > 0) {
      const normalizePlaceType = place => (place.placeType?.[0] ?? '').replace(/-/g, ' ').toLowerCase();
      const getPlaceTypePriority = (place) => {
        const type = normalizePlaceType(place);
        const groupIndex = PLACE_TYPE_PRIORITY_GROUPS.findIndex(group => group.includes(type));
        return groupIndex === -1 ? 99 : groupIndex;
      };
      resultPlace.sort((a, b) => getPlaceTypePriority(a) - getPlaceTypePriority(b));
      resultPlace = resultPlace[0];
    }
    else {
      resultPlace = null;
    }
    if (resultPlace && validatePlaceAsUnicorporatedPart(resultPlace)) {
      const state = resultPlace.id.toString().split('-')?.[0];
      const newUrl = await getPlaceSearch();
      newUrl.addSearch('skipRelates', 1).addSearch('id', `${state}-state-place-${resultPlace.fipsCode}u`);
      resultPlace = await getSearchResults(newUrl);
    }

    const url = new UriJs(resultPlace?.url || '/country/us')
      .search('leftOverlay=Properties')
      .addSearch('folio', item.folio);
    return window.location.assign(url);
  },
};

export default mapboxAPI;
